// reCAPTCHA v3 (invisible) helper.
//
// Loads the Google reCAPTCHA v3 script once and returns a fresh token for a
// given action. If VITE_RECAPTCHA_V3_SITE_KEY is not configured, this is a
// no-op (resolves to null) so forms keep working in dev / before keys are set
// -- the backend's verifyCaptchaV3 middleware fails open in the same way.

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '';

let scriptPromise = null;

function loadScript() {
  if (!SITE_KEY) return Promise.resolve(null);
  if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve(window.grecaptcha);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Get a reCAPTCHA v3 token for the given action.
 * @param {string} action - logical action name (e.g. 'contact', 'report')
 * @returns {Promise<string|null>} token, or null if reCAPTCHA isn't configured
 */
export async function getRecaptchaToken(action = 'submit') {
  if (!SITE_KEY) return null;
  try {
    const grecaptcha = await loadScript();
    if (!grecaptcha) return null;
    return await new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(SITE_KEY, { action }).then(resolve).catch(() => resolve(null));
      });
    });
  } catch (e) {
    // Network/script failure -- let the request proceed; backend decides.
    return null;
  }
}
